import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import type { Device } from "@gooner/shared";
import { api, ApiHttpError } from "../lib/api";
import { type PartyState, usePendingScreen } from "../lib/usePendingScreen";
import { resolveScreenState } from "../control/lib/screenState";
import { type CodeBubble } from "../components/SiteBar";
import { CodeCard } from "../components/CodeCard";
import { DisplayApp } from "./DisplayApp";
import { PendingScreen } from "./PendingScreen";
import { BackToViews } from "../components/BackToViews";
import { rememberHomeView } from "../lib/lastHomeView";
import { screenOriginOf } from "../lib/screenOrigin";

/** How often a screen with nothing showable YET asks again.
 *
 *  Only until it has something. A playing screen holds a display socket and is retargeted
 *  through the controller's `showing` command; a screen with no view has no room to join
 *  and no socket to be told through, so it asks over HTTP. The server points every device
 *  it hands out (see ensureShowing), so this normally runs once. */
const WAIT_POLL_MS = 3000;

/** How often a CLAIMED screen renews its adopt-code entry. The ambient cadence, not the
 *  pairing page's 2s: nobody is standing mid-claim in front of a TV that is already
 *  playing, and the poll's job here is keeping the entry alive so the code on the glass
 *  never rotates under whoever is reading it. */
const CLAIMED_POLL_MS = 30_000;

/**
 * Did the server say this id is not a thing, or did the request merely fail?
 *
 * Only a 404 is the server's verdict. Everything else — a dropped connection, a 500, a
 * dev-server restart mid-poll — is noise, and treating noise as a verdict is how a live
 * screen ends up on the code-entry prompt with no way back but a human walking over to a
 * wall-mounted TV.
 */
const isNotFound = (err: unknown): boolean =>
  err instanceof ApiHttpError && err.status === 404;

/**
 * A screen. It is read across a room and is not an interface, so it has as few states as
 * the truth allows — and "waiting for a view" is not one of them any more. A screen is
 * always playing something; see resolveScreenState.
 *
 * Its identity is the id in this tab's URL and nowhere else: storage is per-origin, so two
 * monitors driven from one computer would otherwise be the same device. Reload keeps it —
 * which is why this screen does not delete itself on `pagehide`; see below.
 */
export function DeviceScreen() {
  const { id } = useParams<{ id: string }>();
  const [device, setDevice] = useState<Device | null>(null);
  const [missing, setMissing] = useState(false);
  const [viewMissing, setViewMissing] = useState(false);
  const [connected, setConnected] = useState(true);
  // Bumped when a mounted panel reports it was never handed a view; re-runs the resolve
  // below rather than trying to reach into it.
  const [nudge, setNudge] = useState(0);

  // Resolve, then keep resolving while there is nothing showable.
  useEffect(() => {
    if (!id) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    /** Does the id this device points at still name a view? A failed request is not a
     *  no: only the server saying 404 is. */
    const viewGone = async (viewId: string): Promise<boolean> => {
      try {
        await api.views.get(viewId);
        return false;
      } catch (err) {
        return isNotFound(err);
      }
    };

    const tick = async () => {
      try {
        const { device: d } = await api.devices.get(id);
        if (!live) return;
        // GET /devices/:id hands back showing.viewId with no existence check, so resolve
        // it here. A temporary view evicts on its TTL and nothing unpoints the devices
        // showing it; without this the screen reports "playing" over a spinner forever.
        const dangling = d.showing ? await viewGone(d.showing.viewId) : false;
        if (!live) return;
        setDevice(d);
        setMissing(false);
        setViewMissing(dangling);
        // Poll only while there is nothing showable. Once it is playing the socket owns
        // the updates and this loop stops, so a wall of screens is not a wall of polls —
        // and a dangling view keeps polling, which is how that screen recovers.
        if (!d.showing || dangling) timer = setTimeout(() => void tick(), WAIT_POLL_MS);
      } catch (err) {
        if (!live) return;
        if (isNotFound(err)) {
          setMissing(true);
          setDevice(null);
          return;
        }
        // A blip. Keep whatever state we had and ask again — ending the loop here is what
        // made one dropped request a permanent demotion to the code entry.
        timer = setTimeout(() => void tick(), WAIT_POLL_MS);
      }
    };

    void tick();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [id, nudge]);

  /*
   * There is deliberately no teardown beacon here.
   *
   * Every device is throwaway now — close a screen and its device is gone, whichever way
   * it was created — so the temptation is a `keepalive` DELETE on the way out. It is
   * wrong: that event fires on a RELOAD exactly as it fires on a close, and a page being
   * unloaded cannot tell which is happening. Beaconing here destroys the device on every
   * F5, and "Reload keeps it" is a stated property of the model's device section.
   *
   * The controller does the deleting, because it is the party that can tell them apart:
   * it watches over time rather than at an instant, and a reload's socket gap closes in
   * about a second while a closed tab's never does. See `sweepDead` in
   * client/src/control/lib/rail.ts.
   *
   * A screen that loses power with no controller open leaves an orphaned row. It never
   * shows in the rail (which filters on presence), and reaping it server-side on
   * `lastSeen` belongs in the protocol plan, not here.
   */

  const state = resolveScreenState({ device, missing, connected, viewMissing });

  if (state === "resolving") return <Black />;
  if (state === "unjoined") return <PendingScreen />;

  return (
    <>
      <Playing
        device={device!}
        onConnected={setConnected}
        // The race the existence check above cannot close: the view expires between that
        // check and the socket's hello. The server answers such a hello with a `t:"error"`
        // frame, which connectDisplay drops, so the socket stays "connected" and this
        // screen would present as playing over a spinner. Re-resolve instead; if the view
        // really is gone the next pass says waiting and the poll takes it from there.
        onNoView={() => setNudge((n) => n + 1)}
      />
      {state === "disconnected" ? <Disconnected name={device!.name} /> : null}
    </>
  );
}

function Black() {
  return <div className="h-screen w-screen bg-[var(--color-ink)]" />;
}

/**
 * The bar's bubble and the big card behind it, shared by the playing and waiting states.
 *
 * The code a CLAIMED screen wears ADOPTS this device: redeeming it hands the device to
 * the redeemer in place — same id, same URL, playback untouched — rather than minting a
 * new one and yanking the tab there, which is what the ambient code on every other page
 * does. usePendingScreen never navigates for an adopt entry (the server answers its poll
 * with no deviceId, because this tab already is the device), so wearing the code costs a
 * playing screen nothing until somebody actually redeems it.
 *
 * The card stays BEHIND the bubble here by default: this screen is playing something, that
 * content is the point, and the code is one press away. (There used to be an
 * `initialShown` flag so the "Waiting for a view" state could greet you with the card — a
 * screen with nothing on it had only one thing to say. That state is gone.)
 *
 * `?code=1` OPENS IT ON ARRIVAL, and it is the mirror of the pending screen's `?code=0`:
 * one URL argument, read once, deciding whether the code greets you. Whoever arrives here
 * from the code card's "Or use this device as the screen" was LOOKING AT THE CODE CARD —
 * they came to pair — so the screen they just made opens on its own card rather than
 * making them find the bubble and press it again. A pending screen that converts itself
 * (signed in) carries whether its card was open, for the same reason: nothing about the
 * glass should change as it becomes a device. The argument is read into state at mount,
 * so pressing × closes it like any other time.
 */
function usePairing(deviceId: string): {
  bubble: CodeBubble;
  card: ReactNode;
  party: PartyState | null;
} {
  const { code, error, party } = usePendingScreen(CLAIMED_POLL_MS, deviceId, undefined, {
    party: true,
  });
  const [params] = useSearchParams();
  const [shown, setShown] = useState(() => params.get("code") === "1");
  return {
    party,
    bubble: {
      // The placeholder rather than nothing: the bubble is the same size before and
      // after the code lands, so the bar does not reflow the moment it arrives.
      text: code ?? "····",
      shown,
      onToggle: () => setShown((s) => !s),
    },
    // No `becomeScreen` on the card: this page already IS a screen.
    card: shown ? <CodeCard code={code} error={error} onClose={() => setShown(false)} /> : null,
  };
}

/** The playing screen: DisplayApp wearing this device's adopt code in the bar's bubble. */
function Playing({
  device,
  onConnected,
  onNoView,
}: {
  device: Device;
  onConnected: (connected: boolean) => void;
  onNoView: () => void;
}) {
  const { bubble, card, party } = usePairing(device.id);
  /*
   * WHERE THIS TAB CAME FROM, carried across the replace that made it a device (see
   * lib/screenOrigin). A signed-in tab is only on the pending screen for a moment, so
   * without this the two things that page promised stopped here: the Quick setup's
   * "Back to views", and Home following the mode — a device switches through
   * `setShowing`, which the pending screen's own memory never sees (2026-10-10 review).
   */
  const origin = screenOriginOf(useLocation().state);
  return (
    <>
      <DisplayApp
        viewId={device.showing!.viewId}
        deviceId={device.id}
        pairing={bubble}
        party={party}
        back={origin.from === "views" ? <BackToViews /> : undefined}
        onPlaying={origin.home ? rememberHomeView : undefined}
        onConnected={onConnected}
        onNoView={onNoView}
      />
      {card}
    </>
  );
}

/*
 * `Waiting` STOOD HERE AND IS DELETED.
 *
 * It was a paired screen with nothing to play: the device name, "Waiting for a view",
 * "Pick one on your remote", and a socket held open purely so the screen would count as
 * present. Every part of that was a workaround for a device the server had handed out
 * unpointed — reachable by pressing "Or use this device as the screen" on the code card,
 * which is to say by the most ordinary act in the product.
 *
 * The one genuinely load-bearing thing it did — hold a socket so a viewless screen still
 * reported present, because presence is computed from open device sockets and the remote's
 * only control required the screen to be online — is now moot: a device always has a view,
 * so it always mounts DisplayApp, which always holds that socket.
 *
 * See `ensureShowing` in server/src/routes/devices.ts for where the guarantee lives.
 */

/**
 * Over the top of whatever was last painted, because the frozen last frame under it is
 * the most useful thing on the screen — it says what this screen WAS showing.
 */
function Disconnected({ name }: { name: string }) {
  return (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-[var(--color-ink)]/95">
      <span className="text-lg uppercase tracking-[0.3em] text-zinc-600">{name}</span>
      <span className="text-4xl text-zinc-300 sm:text-6xl">Disconnected</span>
      <span className="text-xl text-zinc-500">Trying to reconnect…</span>
    </div>
  );
}
